import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import MiningPage from "./pages/MiningPage";
import InventoryPage from "./pages/InventoryPage";
import WalletPage from "./pages/WalletPage";
import WorkersPage from "./pages/WorkersPage";
import TasksPage from "./pages/TasksPage";
import ProfilePage from "./pages/ProfilePage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminEconomy from "./pages/admin/AdminEconomy";
import AdminFinance from "./pages/admin/AdminFinance";

import AdminContent from "./pages/admin/AdminContent";
import AdminAssets from "./pages/admin/AdminAssets";
import AdminShop from "./pages/admin/AdminShop";
import AdminFaucetPay from "./pages/admin/AdminFaucetPay";
import AdminOfferwalls from "./pages/admin/AdminOfferwalls";
import AdminPostbackLogs from "./pages/admin/AdminPostbackLogs";
import AdminGameSettings from "./pages/admin/AdminGameSettings";
import AdminAds from "./pages/admin/AdminAds";
import AdminPTC from "./pages/admin/AdminPTC";
import AdminTasks from "./pages/admin/AdminTasks";
import AdminLeaderboard from "./pages/admin/AdminLeaderboard";
import AdminEmail from "./pages/admin/AdminEmail";
import AdminAntiAbuse from "./pages/admin/AdminAntiAbuse";
import AdminWorkerTiers from "./pages/admin/AdminWorkerTiers";
import AdminSecurity from "./pages/admin/AdminSecurity";
import AdminPaymentProofs from "./pages/admin/AdminPaymentProofs";
import PaymentProofsPage from "./pages/PaymentProofsPage";
import ImpersonatePage from "./pages/ImpersonatePage";
import ImpersonationBanner from "./components/ImpersonationBanner";
import JobsPage from "./pages/JobsPage";
import ShopPage from "./pages/ShopPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import StaticPage from "./pages/StaticPage";
import AdHeadInjector from "./components/AdHeadInjector";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" /></div>;
  if (user) return <Navigate to="/dashboard" />;
  return children;
}

function App() {
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/site-settings`).then(({ data }) => {
      if (data.site_title) document.title = data.site_title;
      if (data.seo_description) {
        const meta = document.getElementById("meta-description");
        if (meta) meta.setAttribute("content", data.seo_description);
      }
      if (data.logo_url || data.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = data.favicon_url || data.logo_url;
      }
    }).catch(() => {});
  }, []);
  return (
    <SiteSettingsProvider>
    <AuthProvider>
      <AdHeadInjector />
      <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify" element={<VerifyEmailPage />} />
          <Route path="/page/:slug" element={<StaticPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/mining" element={<ProtectedRoute><MiningPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
          <Route path="/workers" element={<ProtectedRoute><WorkersPage /></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/economy" element={<ProtectedRoute adminOnly><AdminEconomy /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute adminOnly><AdminFinance /></ProtectedRoute>} />
          <Route path="/admin/content" element={<ProtectedRoute adminOnly><AdminContent /></ProtectedRoute>} />
          <Route path="/admin/assets" element={<ProtectedRoute adminOnly><AdminAssets /></ProtectedRoute>} />
          <Route path="/admin/shop" element={<ProtectedRoute adminOnly><AdminShop /></ProtectedRoute>} />
          <Route path="/admin/faucetpay" element={<ProtectedRoute adminOnly><AdminFaucetPay /></ProtectedRoute>} />
          <Route path="/admin/offerwalls" element={<ProtectedRoute adminOnly><AdminOfferwalls /></ProtectedRoute>} />
          <Route path="/admin/postback-logs" element={<ProtectedRoute adminOnly><AdminPostbackLogs /></ProtectedRoute>} />
          <Route path="/admin/game-settings" element={<ProtectedRoute adminOnly><AdminGameSettings /></ProtectedRoute>} />
          <Route path="/admin/ads" element={<ProtectedRoute adminOnly><AdminAds /></ProtectedRoute>} />
          <Route path="/admin/ptc" element={<ProtectedRoute adminOnly><AdminPTC /></ProtectedRoute>} />
          <Route path="/admin/tasks" element={<ProtectedRoute adminOnly><AdminTasks /></ProtectedRoute>} />
          <Route path="/admin/leaderboard" element={<ProtectedRoute adminOnly><AdminLeaderboard /></ProtectedRoute>} />
          <Route path="/admin/email" element={<ProtectedRoute adminOnly><AdminEmail /></ProtectedRoute>} />
          <Route path="/admin/anti-abuse" element={<ProtectedRoute adminOnly><AdminAntiAbuse /></ProtectedRoute>} />
          <Route path="/admin/worker-tiers" element={<ProtectedRoute adminOnly><AdminWorkerTiers /></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute adminOnly><AdminSecurity /></ProtectedRoute>} />
          <Route path="/admin/payment-proofs" element={<ProtectedRoute adminOnly><AdminPaymentProofs /></ProtectedRoute>} />
          <Route path="/payment-proofs" element={<PaymentProofsPage />} />
          <Route path="/impersonate" element={<ImpersonatePage />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
    </SiteSettingsProvider>
  );
}

export default App;
